import {
  CheckCircleIcon,
  DocumentIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { Text } from '@/components/ui/catalyst/text';
import { cn } from '@/lib/utils';
import type { PackageComponentSchema } from '@/services/api/hipaaPackageService';

import { StaticDocumentLabels } from '../package-edit/constants';
import type { PackagePreviewPaneProps } from '../types';
import { BlobDocumentPreview } from './BlobDocumentPreview';
import { EmptyPreviewState } from './EmptyPreviewState';
import { MemoizedRenderedPdfPreview } from './RenderedPdfPreview';

/** Derive a human-readable title for a single package component. */
const getComponentTitle = (component: PackageComponentSchema): string => {
  switch (component.component_type) {
    case 'template':
      return (
        component.template?.display_name ||
        component.template?.name ||
        'Template Document'
      );
    case 'signed_authorization_merge':
      return (
        component.blob?.original_filename ||
        component.blob?.name ||
        component.template?.display_name ||
        component.template?.name ||
        'Signed Authorization'
      );
    case 'static_document':
      return component.static_document_tag
        ? StaticDocumentLabels[component.static_document_tag]
        : 'Static Document';
    case 'matter_blob':
      return (
        component.blob?.name ||
        component.blob?.original_filename ||
        'Matter Document'
      );
    default:
      return 'Document';
  }
};

/** Derive a status badge config for a single package component. */
const getComponentStatus = (
  component: PackageComponentSchema,
): { label: string; tone: 'success' | 'warning' | 'neutral' } => {
  if (component.status === 'missing_fields') {
    return { label: 'Missing fields', tone: 'warning' };
  }
  if (component.status === 'generating') {
    return { label: 'Generating', tone: 'neutral' };
  }
  if (component.status === 'error') {
    return { label: 'Error', tone: 'warning' };
  }
  return { label: 'Ready', tone: 'success' };
};

/**
 * Preview pane showing all package components.
 * Displays every component preview in a scrollable, stacked list
 * so users can see all their documents at once.
 */
const PackagePreviewPaneComponent: React.FC<PackagePreviewPaneProps> = ({
  components,
  selectedComponentId,
  onSelectComponent,
}) => {
  const componentRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Auto-select first component when none is selected or selection is invalid
  React.useEffect(() => {
    const validComponent = components.find(
      (c) => c.id === selectedComponentId,
    );
    if (!validComponent && components.length > 0) {
      onSelectComponent(components[0].id);
    }
  }, [onSelectComponent, selectedComponentId, components]);

  // Scroll the selected component into view when selection changes
  React.useEffect(() => {
    if (selectedComponentId && componentRefs.current[selectedComponentId]) {
      componentRefs.current[selectedComponentId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [selectedComponentId]);

  const renderPreview = React.useCallback(
    (component: PackageComponentSchema) => {
      if (component.status === 'generating') {
        return (
          <div className="flex h-full min-h-[520px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-700" />
              <Text className="text-sm text-zinc-500">
                Generating preview...
              </Text>
            </div>
          </div>
        );
      }

      if (component.status === 'error') {
        return (
          <div className="flex h-full min-h-[520px] items-center justify-center">
            <div className="text-center">
              <ExclamationTriangleIcon className="mx-auto mb-3 h-10 w-10 text-red-400" />
              <Text className="text-sm font-medium text-zinc-900">
                Preview unavailable
              </Text>
              {component.error_message && (
                <Text className="mt-1 text-sm text-zinc-500">
                  {component.error_message}
                </Text>
              )}
            </div>
          </div>
        );
      }

      if (component.rendered_pdf_url) {
        return (
          <div className="h-full min-h-[720px]">
            <MemoizedRenderedPdfPreview url={component.rendered_pdf_url} />
          </div>
        );
      }

      if (component.component_type === 'matter_blob' && component.blob_id) {
        return (
          <div className="h-full min-h-[720px]">
            <BlobDocumentPreview blobId={component.blob_id} />
          </div>
        );
      }

      return (
        <div className="flex h-full min-h-[520px] items-center justify-center">
          <div className="max-w-sm text-center">
            {component.component_type === 'template' ? (
              <DocumentTextIcon className="mx-auto mb-3 h-10 w-10 text-zinc-300" />
            ) : (
              <DocumentIcon className="mx-auto mb-3 h-10 w-10 text-zinc-300" />
            )}
            <Text className="text-sm font-medium text-zinc-900">
              Preview not available yet
            </Text>
            <Text className="mt-1 text-sm text-zinc-500">
              Regenerate the package after editing to refresh the rendered
              document preview.
            </Text>
          </div>
        </div>
      );
    },
    [],
  );

  return (
    <div className="h-full px-5 py-6">
      {components.length === 0 ? (
        <EmptyPreviewState />
      ) : (
        <div className="flex flex-col gap-6">
          {components.map((component) => {
            const title = getComponentTitle(component);
            const status = getComponentStatus(component);
            const isSelected = component.id === selectedComponentId;

            return (
              <div
                key={component.id}
                ref={(el) => {
                  componentRefs.current[component.id] = el;
                }}
                className={cn(
                  'flex flex-col overflow-hidden rounded-[24px] border bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)] cursor-pointer transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2',
                  isSelected
                    ? 'border-emerald-400 shadow-[0_0_0_1px_rgba(52,211,153,0.3)]'
                    : 'border-zinc-200',
                )}
                role="button"
                tabIndex={0}
                onClick={() => onSelectComponent(component.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectComponent(component.id);
                  }
                }}
              >
                <div
                  className={cn(
                    'flex items-center justify-between border-b px-5 py-3.5',
                    isSelected
                      ? 'border-emerald-200 bg-emerald-50/60'
                      : 'border-zinc-200 bg-zinc-50/60',
                  )}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <DocumentTextIcon
                      className={cn(
                        'h-5 w-5 flex-shrink-0',
                        isSelected ? 'text-emerald-600' : 'text-zinc-400',
                      )}
                    />
                    <Text className="truncate text-[18px] font-medium text-zinc-900">
                      {title}
                    </Text>
                  </div>
                  <div
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium',
                      status.tone === 'success' && 'bg-white text-emerald-700',
                      status.tone === 'warning' && 'bg-white text-amber-700',
                      status.tone === 'neutral' && 'bg-white text-zinc-500',
                    )}
                  >
                    {status.tone === 'success' && (
                      <CheckCircleIcon className="h-4 w-4" />
                    )}
                    {status.tone === 'warning' && (
                      <ExclamationTriangleIcon className="h-4 w-4" />
                    )}
                    {status.label}
                  </div>
                </div>

                <div className="bg-zinc-100/80">
                  <div className="mx-auto max-w-[1280px] px-3 py-3">
                    {renderPreview(component)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PackagePreviewPane = React.memo(PackagePreviewPaneComponent);
